// @ts-nocheck
/**
 * Participants List Component
 * Displays active participants in a collaboration session
 */

import React from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Button,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import { UserAvatar, Microphone, MicrophoneOff, VideoChat, VideoOff, Star, View, Edit } from '@carbon/react/icons';

import type { ParticipantInfo, SessionState } from '../../services/collaborationWebSocketService';

export interface ParticipantsListProps {
  participants: ParticipantInfo[];
  currentUserId: string;
  sessionState: SessionState | null;
  onPromoteParticipant?: (userId: string) => void;
  onRemoveParticipant?: (userId: string) => void;
  onMuteParticipant?: (userId: string) => void;
  className?: string;
}

export const ParticipantsList: React.FC<ParticipantsListProps> = ({
  participants,
  currentUserId,
  sessionState,
  onPromoteParticipant,
  onRemoveParticipant,
  onMuteParticipant,
  className = '',
}) => {
  // Helper function to get participant status
  const getParticipantStatus = (participant: ParticipantInfo) => {
    const statuses = [];

    if (participant.userRole === 'teacher' || participant.userRole === 'tutor') {
      statuses.push({ label: 'Instructor', type: 'purple' });
    }

    if (participant.capabilities.includes('session_control')) {
      statuses.push({ label: 'Moderator', type: 'blue' });
    }

    if (participant.capabilities.includes('collaborative_editing')) {
      statuses.push({ label: 'Editor', type: 'green' });
    }

    if (participant.capabilities.includes('voice_chat')) {
      statuses.push({ label: 'Voice', type: 'cyan' });
    }

    if (!statuses.length) {
      statuses.push({ label: 'Observer', type: 'gray' });
    }

    return statuses;
  };

  // Helper function to format join time
  const formatJoinTime = (joinedAt: string) => {
    const joinTime = new Date(joinedAt);
    const now = new Date();
    const diffMs = now.getTime() - joinTime.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just joined';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m ago`;
  };

  // Check if current user can manage participants
  const canManageParticipants = sessionState?.participants
    .find(p => p.userId === currentUserId)
    ?.capabilities.includes('participant_management');

  const headers = [
    { key: 'avatar', header: '' },
    { key: 'name', header: 'Name' },
    { key: 'role', header: 'Role' },
    { key: 'status', header: 'Status' },
    { key: 'joinedAt', header: 'Joined' },
    { key: 'actions', header: '' },
  ];

  const rows = participants.map(participant => {
    const statuses = getParticipantStatus(participant);
    const isCurrentUser = participant.userId === currentUserId;

    return {
      id: participant.userId,
      avatar: (
        <div className="participants-list__avatar">
          <UserAvatar size={32} />
          {isCurrentUser && (
            <div className="participants-list__current-user-indicator">
              <Star size={16} />
            </div>
          )}
        </div>
      ),
      name: (
        <div className="participants-list__name">
          <span className="participants-list__display-name">
            {participant.userName}
            {isCurrentUser && ' (You)'}
          </span>
        </div>
      ),
      role: (
        <Tag type="blue" size="sm">
          {participant.userRole}
        </Tag>
      ),
      status: (
        <div className="participants-list__status">
          {statuses.map((status, index) => (
            <Tag key={index} type={status.type as any} size="sm" className="participants-list__status-tag">
              {status.label}
            </Tag>
          ))}
        </div>
      ),
      joinedAt: <span className="participants-list__join-time">{formatJoinTime(participant.joinedAt)}</span>,
      actions: (
        <div className="participants-list__actions">
          {/* Voice indicators */}
          {participant.capabilities.includes('voice_chat') && (
            <div className="participants-list__voice-indicators">
              <Microphone size={16} className="participants-list__voice-icon" />
            </div>
          )}

          {/* Management actions */}
          {canManageParticipants && !isCurrentUser && (
            <OverflowMenu ariaLabel="Participant actions" iconDescription="More actions" size="sm">
              {onPromoteParticipant && !participant.capabilities.includes('session_control') && (
                <OverflowMenuItem itemText="Make Moderator" onClick={() => onPromoteParticipant(participant.userId)} />
              )}

              {onMuteParticipant && participant.capabilities.includes('voice_chat') && (
                <OverflowMenuItem itemText="Mute Participant" onClick={() => onMuteParticipant(participant.userId)} />
              )}

              {onRemoveParticipant && (
                <OverflowMenuItem
                  itemText="Remove from Session"
                  onClick={() => onRemoveParticipant(participant.userId)}
                  requireConfirmation
                />
              )}
            </OverflowMenu>
          )}
        </div>
      ),
    };
  });

  return (
    <div className={`participants-list ${className}`}>
      <div className="participants-list__summary">
        <p>
          <strong>{participants.length}</strong> participant{participants.length !== 1 ? 's' : ''}
          {sessionState && (
            <span>
              {' '}
              in session <strong>{sessionState.sessionId}</strong>
            </span>
          )}
        </p>
      </div>

      <DataTable rows={rows} headers={headers} size="sm">
        {({ rows, headers, getHeaderProps, getRowProps, getTableProps, getTableContainerProps }) => (
          <div {...getTableContainerProps()}>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow
                    {...getRowProps({ row })}
                    className={row.id === currentUserId ? 'participants-list__current-user-row' : ''}
                  >
                    {row.cells.map(cell => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DataTable>

      {participants.length === 0 && (
        <div className="participants-list__empty">
          <p>No participants in this session yet.</p>
        </div>
      )}

      {/* Session Info */}
      {sessionState && (
        <div className="participants-list__session-info">
          <h4>Session Information</h4>
          <ul>
            <li>
              Status: <strong>{sessionState.sessionStatus}</strong>
            </li>
            <li>
              Features: <strong>{sessionState.enabledFeatures.join(', ')}</strong>
            </li>
            {sessionState.documents.length > 0 && (
              <li>
                Documents: <strong>{sessionState.documents.length}</strong>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ParticipantsList;
